import { Component, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { ReadonlyController } from "@odx/angular";
import { CheckboxComponent } from "@odx/angular/components/checkbox";
import { FormDirective, FormFieldComponent, FormFieldControlDirective } from "@odx/angular/components/form-field";
import { ModalService } from "@odx/angular/components/modal";
import { TabBarModule } from "@odx/angular/components/tab-bar";
import { FloorplanCustomPartNumbersComponent } from "@project/floorplanner/floorplan-custom-part-numbers/floorplan-custom-part-numbers.component";
import { FloorplanTransmitterRadiusFormComponent } from "../floorplan-transmitter-diameter-form/floorplan-transmitter-radius-form.component";

@Component({
  selector: "app-floorplan-item-detail-transmitter-form",
  imports: [
    UiKitModule,
    FormDirective,
    FormFieldComponent,
    FormsModule,
    FormFieldControlDirective,
    ReactiveFormsModule,
    CheckboxComponent,
    ReadonlyController,
    FloorplanCustomPartNumbersComponent,
    FloorplanTransmitterRadiusFormComponent,
    TabBarModule,
  ],
  templateUrl: "./floorplan-transmitter-detail-form.component.html",
  styleUrl: "./floorplan-transmitter-detail-form.component.scss",
  standalone: true,
})
export class FloorplanTransmitterDetailFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private modalService = inject(ModalService);

  readonly transmitter = input.required<FloorplanTransmitter | FloorplanTransmitterPlaceholder>();

  protected readonly selectedTabIndex = signal<number>(0);
  protected editTransmitterForm!: FormGroup;
  protected detectionRangeControl = new FormControl<boolean>(true);

  ngOnInit() {
    this.initForm();
  }

  private initForm() {
    this.initDetectionRangeForm();
    this.editTransmitterForm = this.formBuilder.group({
      bmk: [this.transmitter().bmk],
      sector: [this.transmitter().sector],
      installationHeight: [this.transmitter().installationHeight],
      exProtection: [this.transmitter().exProtection],
      hasRemoteSensor: [this.transmitter().remoteSensor],
    });

    this.editTransmitterForm.valueChanges.subscribe(() => {
      if (!this.editTransmitterForm.valid || this.editTransmitterForm.pristine) return;
      this.update();
    });
  }

  private initDetectionRangeForm() {
    const transmitter = this.transmitter();

    if (!(transmitter instanceof FloorplanTransmitter)) {
      return;
    }

    this.setDetectionRangeControlValue(!transmitter.isDetectionRangeOverridden);

    transmitter.monitoringAreaRadiusChanged.subscribe(() => {
      this.setDetectionRangeControlValue(!transmitter.isDetectionRangeOverridden);
    });
  }

  private update() {
    this.transmitter().bmk = this.editTransmitterForm.value.bmk;
    this.transmitter().sector = this.editTransmitterForm.value.sector;
    this.transmitter().installationHeight = this.editTransmitterForm.value.installationHeight;
    this.transmitter().exProtection = this.editTransmitterForm.value.exProtection;

    if (!this.transmitter().remoteSensor && this.editTransmitterForm.value.hasRemoteSensor) {
      this.transmitter().attachRemoteSensor();
    }

    if (!!this.transmitter().remoteSensor && !this.editTransmitterForm.value.hasRemoteSensor) {
      this.transmitter().detachRemoteSensor();
    }
  }

  protected isTransmitter(): boolean {
    return this.transmitter() instanceof FloorplanTransmitter;
  }

  protected isDetectionRangeOverridden(): boolean {
    const transmitter = this.transmitter();

    if (!(transmitter instanceof FloorplanTransmitter)) {
      return false;
    }

    return transmitter.isDetectionRangeOverridden;
  }

  protected onDetectionRangeToggle(checked: boolean) {
    const transmitter = this.transmitter();

    if (!(transmitter instanceof FloorplanTransmitter)) {
      return;
    }

    if (!checked) {
      this.setDetectionRangeControlValue(false);
      transmitter.setDetectionRangeOverrideFromCurrent();
      return;
    }

    const currentValue = this.detectionRangeControl.value ?? true;
    const defaultRadius = transmitter.config.defaultMonitoringAreaRadius;

    if (this.isCurrentRadiusDefaultRadius(transmitter.referenceMonitoringAreaRadius, defaultRadius)) {
      this.setDetectionRangeControlValue(true);
      transmitter.resetDetectionRangeToDefault();
      return;
    }

    this.setDetectionRangeControlValue(currentValue);
    this.openModal(currentValue, transmitter);
  }

  private openModal(currentValue: boolean, transmitter: FloorplanTransmitter) {
    this.modalService
      .open(ConfirmModalComponent, {
        data: {
          headerText: $localize`:@@floorplan.details.transmitter.detectionRange.modal.header:Auf Standardwert zurücksetzen`,
          text: $localize`:@@floorplan.details.transmitter.detectionRange.modal.confirmReset:Der ${$localize`:@@editTransmitterForm.label.diameter:Durchmesser`} wird auf den Standardwert der Konfiguration zurückgesetzt. Fortfahren?`,
          confirmButtonText: $localize`:@@floorplan.details.transmitter.detectionRange.modal.confirm:Bestätigen`,
        },
      })
      .onClose$.subscribe((result) => {
        if (result !== "confirmed") {
          this.setDetectionRangeControlValue(currentValue);
          return;
        }

        this.setDetectionRangeControlValue(true);
        transmitter.resetDetectionRangeToDefault();
      });
  }

  private isCurrentRadiusDefaultRadius(current: number, defaultRadius: number) {
    return Math.abs(current - defaultRadius) <= 0.001;
  }

  private setDetectionRangeControlValue(value: boolean) {
    this.detectionRangeControl.setValue(value, { emitEvent: false });
  }
}
